/**
 * @fileoverview API utilities and React Query hooks for unmatched listings.
 * @module features/sku-alias/api/needsReview
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import apiClient from '@/lib/apiClient'
import { getAllVariants, type Variant } from '@/lib/variantService'
import {
  productIdSchema,
  type ProductId,
  unmatchedListingSchema,
  unmatchedListingPageSchema,
  type UnmatchedListingPage,
} from '@/features/sku-alias/schemas/unmatchedListingSchema'

const catalogProductIdSchema = z.string().uuid()
export type CatalogProductId = z.infer<typeof catalogProductIdSchema>

/**
 * Normalize text for diacritics-insensitive search.
 * Removes accents/diacritics so "ULKA" matches "ÜLKA", "lodz" matches "łódź", etc.
 */
function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
    .replace(/ł/g, 'l') // Polish ł (NFD doesn't decompose it)
    .replace(/đ/g, 'd')
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/œ/g, 'oe')
    .replace(/ß/g, 'ss')
}

const needsReviewCountSchema = z.object({
  count: z.number().int().nonnegative(),
})

/**
 * AI SKU matching result schema.
 */
export const aiSkuMatchingResultSchema = z.object({
  skusProcessed: z.number(),
  suggestionsApplied: z.number(),
  invalidMatches: z.number(),
  message: z.string(),
})
export type AiSkuMatchingResult = z.infer<typeof aiSkuMatchingResultSchema>

/**
 * Allowed sort keys for unmatched listing queries.
 */
export type NeedsReviewSortKey =
  | 'externalSlug'
  | 'source'
  | 'firstSeen'
  | 'lastSeen'
  | 'occurrenceCount'
  | 'totalUnattributed'

/**
 * Query parameters for fetching unmatched listings.
 */
export interface NeedsReviewQueryParams {
  /** Zero-based page number */
  page?: number
  /** Page size */
  size?: number
  /** Column to sort */
  sort?: NeedsReviewSortKey
  /** Sort direction */
  direction?: 'asc' | 'desc'
  /** Optional filter by source channel */
  sources?: string[]
  /** Optional filter for minimum occurrence count */
  minOccurrences?: number
  /** Optional filter for maximum occurrence count */
  maxOccurrences?: number
  /** Optional ISO date range filter for first seen */
  firstSeenFrom?: string
  firstSeenTo?: string
  /** Optional ISO date range filter for last seen */
  lastSeenFrom?: string
  lastSeenTo?: string
}

/**
 * Fetch unmatched listings page from backend.
 *
 * @param params Query parameters for pagination, sorting, and filtering.
 * @returns Parsed unmatched listing page.
 */
export async function fetchUnmatchedListings(
  params: NeedsReviewQueryParams
): Promise<UnmatchedListingPage> {
  const response = await apiClient.get('/sku-inbox', {
    params,
  })
  if (Array.isArray(response.data)) {
    const content = z.array(unmatchedListingSchema).parse(response.data)
    return {
      content,
      totalElements: content.length,
      totalPages: 1,
      size: content.length,
      number: 0,
      first: true,
      last: true,
    }
  }
  return unmatchedListingPageSchema.parse(response.data)
}

/**
 * Fetch unresolved listing count for navigation badge.
 *
 * @returns The number of unmatched listings.
 */
export async function fetchUnmatchedListingCount(): Promise<number> {
  const response = await apiClient.get('/sku-inbox/count')
  const parsed = needsReviewCountSchema.parse(response.data)
  return parsed.count
}

/**
 * Search catalog products for manual linking.
 *
 * @param query Free-text query to search by slug or display names.
 * @returns Matching variant results.
 */
export async function searchCatalogProducts(query: string): Promise<Variant[]> {
  if (!query) return []
  const allVariants = await getAllVariants()
  const queryTokens = normalizeForSearch(query).split(/\s+/).filter(Boolean)
  if (queryTokens.length === 0) return []

  return allVariants
    .filter(variant => {
      // Combine all searchable fields into one normalized string
      const searchableText = normalizeForSearch(
        [variant.variantSkuKey, variant.name, variant.productName].filter(Boolean).join(' ')
      )
      // All query tokens must appear somewhere in the searchable text
      return queryTokens.every(token => searchableText.includes(token))
    })
    .slice(0, 10)
}

/**
 * Payload for linking an unmatched listing to an existing product.
 */
export interface AssignAliasPayload {
  slug: string
  productId: ProductId
}

/**
 * Link an unmatched listing to an existing product.
 *
 * @param payload Assignment payload with listing and product identifiers.
 */
export async function assignListingToProduct(payload: AssignAliasPayload): Promise<void> {
  const listingSlug = z.string().min(1).parse(payload.slug)
  const productId = productIdSchema.parse(payload.productId)
  // Use the new resolve-slug endpoint
  await apiClient.post('/staging-external-skus/resolve-slug', {
    slug: listingSlug,
    masterVariantId: productId,
  })
}

const createMasterResponseSchema = z.object({
  masterVariantId: productIdSchema,
  id: productIdSchema.optional(),
})

/**
 * Payload for creating a new variant under an existing product from an unmatched listing.
 */
export interface CreateVariantFromListingPayload {
  entryId: number
  productId: CatalogProductId
  sku: string
  name: string
  imageUrl?: string | null
  originalCost?: number | null
  originalCurrency?: string | null
}

/**
 * Create a new master Variant under an existing product and link the listing to it.
 *
 * @param payload Data required to create the variant.
 * @returns The newly created variant identifier.
 */
export async function createVariantFromListing(
  payload: CreateVariantFromListingPayload
): Promise<ProductId> {
  const productId = catalogProductIdSchema.parse(payload.productId)
  const response = await apiClient.post(`/staging-external-skus/${payload.entryId}/create-master`, {
    productId,
    sku: payload.sku,
    name: payload.name,
    imageUrl: payload.imageUrl ?? undefined,
    originalCost: payload.originalCost ?? undefined,
    originalCurrency: payload.originalCurrency ?? undefined,
  })

  const parsed = createMasterResponseSchema.safeParse(response.data)
  if (parsed.success) {
    if (parsed.data.masterVariantId) return parsed.data.masterVariantId
    if (parsed.data.id) return parsed.data.id
  }

  const directId = productIdSchema.safeParse(response.data)
  if (directId.success) return directId.data

  const nestedId = productIdSchema.safeParse((response.data as { id?: unknown })?.id)
  if (nestedId.success) return nestedId.data

  throw new Error('Unable to determine created product identifier.')
}

/**
 * Generate AI suggestions for unresolved SKUs.
 * Triggers LLM-based matching and stores suggestions in suggested_variant_id.
 *
 * @returns AI matching result with counts
 */
export async function generateSkuAiSuggestions(): Promise<AiSkuMatchingResult> {
  const response = await apiClient.post('/staging-external-skus/ai-suggestions/generate')
  return aiSkuMatchingResultSchema.parse(response.data)
}

/**
 * Clear AI suggestion for a specific SKU slug.
 * Allows it to be re-suggested on the next AI run.
 *
 * @param slug The external slug to clear the suggestion for
 */
export async function clearSkuSuggestion(slug: string): Promise<void> {
  await apiClient.delete(`/staging-external-skus/${encodeURIComponent(slug)}/suggestion`)
}

export const NEEDS_REVIEW_QUERY_KEY = ['needs-review', 'listings'] as const
export const NEEDS_REVIEW_COUNT_QUERY_KEY = ['needs-review', 'count'] as const

/**
 * React Query hook for fetching unmatched listings.
 *
 * @param params Query parameters for pagination, sorting, and filtering.
 * @param options Additional query configuration overrides.
 * @returns The query result containing the unmatched listings page.
 * @example
 * ```tsx
 * const { data, isLoading } = useNeedsReviewQuery({ page: 0, size: 20 })
 * ```
 */
export function useNeedsReviewQuery(
  params: NeedsReviewQueryParams,
  options?: Omit<UseQueryOptions<UnmatchedListingPage, Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<UnmatchedListingPage, Error> {
  return useQuery<UnmatchedListingPage, Error>({
    queryKey: [...NEEDS_REVIEW_QUERY_KEY, params] as const,
    queryFn: () => fetchUnmatchedListings(params),
    placeholderData: (previousData: UnmatchedListingPage | undefined) => previousData,
    ...options,
  })
}

/**
 * React Query hook to retrieve the unresolved listing count.
 *
 * @returns Query result containing the unresolved count.
 */
export function useNeedsReviewCountQuery(): UseQueryResult<number, Error> {
  return useQuery<number, Error>({
    queryKey: NEEDS_REVIEW_COUNT_QUERY_KEY,
    queryFn: fetchUnmatchedListingCount,
    refetchInterval: false,
  })
}

/**
 * React Query hook for catalog product search.
 *
 * @param query The search string supplied by the user.
 * @returns Matching variants.
 */
export function useCatalogSearchQuery(query: string): UseQueryResult<Variant[], Error> {
  return useQuery<Variant[], Error>({
    queryKey: ['variant', 'search', query],
    queryFn: () => searchCatalogProducts(query),
    enabled: query.length >= 2,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })
}

/**
 * Mutation hook for assigning an unmatched listing to an existing product.
 *
 * @returns Mutation result for alias linking.
 */
export function useAssignAliasMutation(): UseMutationResult<void, Error, AssignAliasPayload> {
  const queryClient = useQueryClient()
  return useMutation<void, Error, AssignAliasPayload>({
    mutationFn: assignListingToProduct,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_COUNT_QUERY_KEY }),
      ])
      queryClient.invalidateQueries({ queryKey: ['variants'] })
      // Invalidate all variant resolution status queries since alias assignment affects them
      queryClient.invalidateQueries({ queryKey: ['variant-resolution-status'] })
    },
  })
}

/**
 * Mutation hook for creating a new variant from an unmatched listing.
 *
 * @returns Mutation result containing the new product identifier on success.
 */
export function useCreateVariantFromListingMutation(): UseMutationResult<
  ProductId,
  Error,
  CreateVariantFromListingPayload
> {
  const queryClient = useQueryClient()
  return useMutation<ProductId, Error, CreateVariantFromListingPayload>({
    mutationFn: createVariantFromListing,
    onSuccess: async productId => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_COUNT_QUERY_KEY }),
      ])
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['variants'] })
      queryClient.invalidateQueries({ queryKey: ['variant', productId] })
      queryClient.invalidateQueries({ queryKey: ['variant', 'search'] })
      // Invalidate all variant resolution status queries since product creation affects them
      queryClient.invalidateQueries({ queryKey: ['variant-resolution-status'] })
    },
  })
}

/**
 * Mutation hook for generating AI suggestions for unresolved SKUs.
 * Triggers LLM-based matching and refreshes the listing on success.
 *
 * @returns Mutation result containing AI matching stats on success
 */
export function useGenerateSkuAiSuggestionsMutation(): UseMutationResult<
  AiSkuMatchingResult,
  Error,
  void
> {
  const queryClient = useQueryClient()
  return useMutation<AiSkuMatchingResult, Error, void>({
    mutationFn: generateSkuAiSuggestions,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_COUNT_QUERY_KEY })
    },
  })
}

/**
 * Mutation hook for clearing AI suggestion on a SKU slug.
 * Allows the slug to be re-suggested on the next AI generation run.
 *
 * @returns Mutation result
 */
export function useClearSkuSuggestionMutation(): UseMutationResult<
  void,
  Error,
  { slug: string }
> {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { slug: string }>({
    mutationFn: ({ slug }) => clearSkuSuggestion(slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_QUERY_KEY })
    },
  })
}
